import { configureStore } from "@reduxjs/toolkit";
import router from "../components/Routers/Router";
import { combineReducers } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { createTransform, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import addressBookSliceReducer from "../components/AddressBook/AddressBookSlice";
import GroupDataSliceReducer from "../components/AddressBook/GroupDataSlice";
import AlertSlice from "../components/Alert/AlertSlice";
import authSliceReducer from "../components/Auth/AuthSlice";
import BSFMSlice from "../components/BSFM/BSFMSlice";
import dashboardReducer from "../components/Dashboard/DashboardSlice";
import DltSlice from "../components/DLT/DltSlice";
import DownloadsSliceReducer from "../components/Downloads/DownloadsSlice";
import AutoScheduleSlice from "../components/Message/AutoSchedule/AutoScheduleSlice";
import BulkSmsSliceReducer from "../components/Message/BulkSms/BulkSmsSlice";
import BulkBySenderSlice from "../components/Message/BulkBySender/BulkBySenderSlice";
import MessageSliceReducer from "../components/Message/MessageSlice";
import resendSlice from "../components/Message/Resend/ResendSlice";
import SingleSmsSliceReducer from "../components/Message/SingleSms/SingleSmsSlice";
import NetworkSlice from "../components/Network/NetworkSlice";
import ProfileSliceReducer from "../components/Profile/ProfileSlice";
import ReportSliceReducer from "../components/Report/ReportSlice";
import RoutingSlice from "../components/Routing/RoutingSlice";
import SaleSliceReducer from "../components/Sales/SalesSlice";
import SMSCSlice from "../components/SMSC/SMSCSlice";
import SubscriptionSliceReducer from "../components/Subscription/SubscriptionSlice";
import TemplateSliceReducer from "../components/Templates/TemplateSlice";
import TwoWaySliceReducer from "../components/TwoWay/TwoWaySlice";
import UpdatePasswordReducer from "../components/UpdatePassword/UpdatePasswordSlice";
import UpdateApiKeyReducer from "../components/UpdateApiKey/UpdateApiKeySlice";
import UserSlice from "../components/User/UserSlice";
import BulkEditSlice from "../components/Utility/Edit/Bulk/BulkEditSlice";
import ScheduleEditSlice from "../components/Utility/Edit/Schedule/ScheduleEditSlice";
import WalletReducer from "../components/Wallet/WalletSlice";
import downloadProgressReducer from "../Layouts/Navbar/DownloadProgress/DownloadLogSlice";
import NotificationReducer from "../Layouts/Navbar/Notifications/NotificationSlice";
import { clearAuthStorage, kindOf, showToast, extractApiMessage, getEnglishToastMessage } from "../lib/utility";
import commonUtilitySlice from "./commonUtilitySlice";
import CompanySliceReducer from "../components/Company/CompanySlice";
import VendorSliceReducer from "../components/Vendor/VendorSlice";
import PCapSliceReducer from "../components/PCap/PCapSlice";
import BillingSliceReducer from "../components/Billing/BillingSlice";

const appReducer = combineReducers({
  auth: authSliceReducer,
  message: MessageSliceReducer,
  sendSms: SingleSmsSliceReducer,
  sendBulk: BulkSmsSliceReducer,
  bulkBySender: BulkBySenderSlice,
  profile: ProfileSliceReducer,
  template: TemplateSliceReducer,
  addressBook: addressBookSliceReducer,
  Group: GroupDataSliceReducer,
  Downloads: DownloadsSliceReducer,
  twoway: TwoWaySliceReducer,
  report: ReportSliceReducer,
  subscription: SubscriptionSliceReducer,
  dashboard: dashboardReducer,
  wallet: WalletReducer,
  notification: NotificationReducer,
  updatePassword: UpdatePasswordReducer,
  updateApiKey: UpdateApiKeyReducer,
  scheduleEdit: ScheduleEditSlice,
  bulkEdit: BulkEditSlice,
  autoSchedule: AutoScheduleSlice,
  sales: SaleSliceReducer,
  bsfm: BSFMSlice,
  user: UserSlice,
  routing: RoutingSlice,
  smsc: SMSCSlice,
  alert: AlertSlice,
  dlt: DltSlice,
  network: NetworkSlice,
  resend: resendSlice,
  downloadProgress: downloadProgressReducer,
  commonUtility: commonUtilitySlice,
  company: CompanySliceReducer,
  vendor: VendorSliceReducer,
  pcap: PCapSliceReducer,
  billing: BillingSliceReducer,
});

export const RESET_APP_STATE = "app/resetState";
export const resetAppState = () => ({ type: RESET_APP_STATE });

const rootReducer = (state, action) => {
  if (action?.type === RESET_APP_STATE) {
    // Drop all in-memory reducer state so next user starts clean.
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

// Request flags must not survive reload — e.g. loginUser.pending sets
// isFetching true, and without a live request it never clears after rehydrate.
// Persisted ABAC permissions stay (so gating works instantly after reload),
// but the "loaded" flags reset so the session re-fetches the fresh tree once.
const ABAC_REQUEST_FLAGS_RESET = {
  isFetchingAbacSession: false,
  abacSessionLoaded: false,
  abacSessionOk: false,
};

function sanitizePersistedAuth(state) {
  if (!state || typeof state !== "object") return state;
  const next = { ...state, isFetching: false, ...ABAC_REQUEST_FLAGS_RESET };
  if (next.user && typeof next.user === "object" && !next.user.abacMemberLogin) {
    next.user = {
      ...next.user,
      isMultiAccessMember: false,
      abacMemberLogin: false,
    };
  }
  return next;
}

const authRequestFlagsTransform = createTransform(
  (inboundState) => sanitizePersistedAuth(inboundState),
  (outboundState) => sanitizePersistedAuth(outboundState),
  { whitelist: ["auth"] }
);

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "subscription"],
  blacklist: [],
  transforms: [authRequestFlagsTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
      // serializableCheck: {
      //   // ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      //   ignoredActions: [PERSIST, REGISTER, PURGE],
      // },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

export const persistor = persistStore(store, composeWithDevTools());

async function checkInternetConnection() {
  return await fetch("https://www.google.com/", {
    method: "HEAD",
    mode: "no-cors",
  })
    .then(() => true)
    .catch(() => false);
}

export const isFunction = (cb) => kindOf(cb, "function");

export const commonMiddleware = async (action, options = {}) => {
  const publicRoute =
    window.location.pathname == "/login" || window.location.pathname == "/";
  const guestRoute =
    window.location.pathname == "/update-password" ||
    window.location.pathname == "/update-api-key";
  const messageTypeMap = {
    create: ["Created", "Creating"],
    update: ["Updated", "Updating"],
    delete: ["Deleted", "Deleting"],
    get: ["Get", "Getting"],
  };
  let { type, name, cb, error, callBacks, customMessage } = options;

  let status = (action.payload?.status ?? action.payload?.statusCode ?? action.payload?.data?.status ?? action.payload?.data?.data?.status ?? action.payload?.errorCode)?.toString();
  let message = extractApiMessage(action.payload, customMessage);
  callBacks = callBacks === undefined ? {} : callBacks;

  if (action.payload?.message === "Download Canceled") {
    return;
  }

  if (
    action?.error &&
    !["404", "401", "503", "400", "409", "502", "429"].includes(status)
  ) {
    if (!(await checkInternetConnection())) {
      if (!globalThis.isOffline || Date.now() > +globalThis.isOffline + 15000) {
        showToast(getEnglishToastMessage("toast.internetDisconnected"), "warning");
        globalThis.isOffline = Date.now();
      }
      return;
    } else {
      if (action?.error?.message === "Netork Error") {
        showToast(getEnglishToastMessage("toast.networkIssue"), "warning");
      }
      globalThis.isOffline = false;
    }
  } else globalThis.isOffline = false;

  if (
    status === "401" ||
    (action.payload?.error === "Unauthorized" &&
      action.payload?.message?.includes("JWT"))
  ) {
    if (window.location.pathname != "/login" || window.location.pathname != "/")
      showToast(
        message ??
          (guestRoute
            ? getEnglishToastMessage("toast.unauthorized")
            : getEnglishToastMessage("toast.userSessionExpired")),
        "warning"
      );
    setTimeout(() => {
      store.dispatch(resetAppState());
      clearAuthStorage();
      persistor.purge();
      globalThis.profileData = null;
      document.body.classList.remove("dark-mode");
      if (!publicRoute) router.navigate("/login");
      globalThis.profileData = null;
    }, 1000);

    return;
  }

  if (status === "503") {
    showToast(
      message ?? getEnglishToastMessage("toast.serviceInMaintenance", { name }),
      "warning"
    );
    return;
  }

  if (status === "429") {
    showToast(
      message ?? getEnglishToastMessage("toast.tooManyRequests", { name }),
      "warning"
    );
    return;
  }

  if (status === "400") {
    showToast(
      message ?? getEnglishToastMessage("toast.badRequestFor", { name }),
      "warning"
    );
    return;
  }

  if (status === "404" || action.payload?.errorCode === "NOT_FOUND" || action.payload?.error?.includes("Not Found")) {
    const isWarningApi = [
      "Message",
      "Template List",
      "Templates",
      "Campaign List",
      "Schedule Manager ",
      "Two Way List",
      "Subscription list",
      "Campaign Aborted Report",
      "Campaign Report",
      "Schedule Report",
      "Delivery Report",
      "Summary Delivery",
      "Batch Summary Report",
      "Delivery Advance Report",
      "Content Report",
      "Latency Report",
      "Consumption Report",
      "Lookup Report",
      "Address Book Groups",
      "Error Code List",
      "Submission Report",
      "SMSC List",
      "Coverage Report",
      "Performance User Report",
      "Performance Route",
      "TPS Report",
      "EDR Report",
      "Vendor Summary Report",
      "Sales",
      "User List",
      "Aggregator Report",
      "Delivery SMSC Report",
      "Profit Report",
      "Blocked Report",
      "Saved Filter",
      "Dashboard Data",
      "Account Summary",
      "Recent Activity",
      "Performance Data",
      "Traffic Data",
      "Profile",
      "profile"
    ].map(v => v.trim().toLowerCase()).includes(name?.trim().toLowerCase());
    const toastType = "warning";

    !callBacks.noToast &&
      showToast(
        message ?? getEnglishToastMessage("toast.noDataFoundFor", { name }),
        toastType
      );
    if (isFunction(callBacks.modal)) callBacks.modal();
    if (isFunction(callBacks.notFound)) callBacks.notFound();
    return;
  }

  if (status === "409") {
    !callBacks.noToast &&
      showToast(
        message ?? getEnglishToastMessage("toast.duplicate", { name }),
        "warning"
      );
    return;
  }

  if (["create", "update"].includes(type) && message?.includes("Duplicate")) {
    !callBacks.noToast &&
      showToast(message, "warning");
    return;
  }

  if (action.error || (action.payload && action.payload?.error)) {
    const isWarningApi = [
      "Message",
      "Template List",
      "Templates",
      "Campaign List",
      "Schedule Manager ",
      "Two Way List",
      "Subscription list",
      "Campaign Aborted Report",
      "Campaign Report",
      "Schedule Report",
      "Delivery Report",
      "Summary Delivery",
      "Batch Summary Report",
      "Delivery Advance Report",
      "Content Report",
      "Latency Report",
      "Consumption Report",
      "Lookup Report",
      "Address Book Groups",
      "Error Code List",
      "Submission Report",
      "SMSC List",
      "Coverage Report",
      "Performance User Report",
      "Performance Route",
      "TPS Report",
      "EDR Report",
      "Vendor Summary Report",
      "Sales",
      "User List",
      "Aggregator Report",
      "Delivery SMSC Report",
      "Profit Report",
      "Blocked Report",
      "Saved Filter",
      "Dashboard Data",
      "Account Summary",
      "Recent Activity",
      "Performance Data",
      "Traffic Data",
      "Profile",
      "profile"
    ].map(v => v.trim().toLowerCase()).includes(name?.trim().toLowerCase());
    const is4xx = status?.startsWith("4");
    const toastType = (isWarningApi && is4xx) ? "warning" : "error";

    !callBacks.noToast &&
      showToast(
        message ??
          error ??
          getEnglishToastMessage("toast.errorWhile", {
            action: messageTypeMap[type][1].toLowerCase(),
            name,
          }),
        toastType
      );
    if (isFunction(callBacks.modal)) callBacks.modal();
    return;
  }

  if (type !== "get") {
    !callBacks.noToast &&
      showToast(
        message ??
          getEnglishToastMessage("toast.successfully", {
            action: messageTypeMap[type][0],
            name,
          }),
        "success"
      );
    if (isFunction(callBacks.modal)) callBacks.modal();

    if (!callBacks.isLast) {
      setTimeout(() => {
        isFunction(callBacks.refresh) && callBacks.refresh();
      }, callBacks.refreshTime ?? 1200);
    }
  }

  isFunction(cb) && cb(action);
};
export const middleware = (
  action,
  name,
  cb,
  error,
  callBacks,
  customMessage
) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "get",
    customMessage,
  });
};
export const middlewareCreate = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "create",
  });
};
export const middlewareUpdate = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "update",
  });
};
export const middlewareDelete = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "delete",
  });
};


